/**
 * Estilos da pagina publica /p/orcamento/:token.
 *
 * Standalone (sem chrome do ERP). Mobile-first. Paleta sobria.
 */
import styled from "styled-components";

export const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 32px;
  font-family: 'Montserrat', sans-serif;
  color: #222;
`;

export const Header = styled.header`
  background: #1f2937;
  color: #fff;
  padding: 16px 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;

export const HeaderInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Title = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
`;

export const Subtitle = styled.span`
  font-size: 0.9rem;
  color: #cbd5e1;
  font-weight: 500;
`;

export const Card = styled.div`
  max-width: 760px;
  margin: 16px auto 0;
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);

  @media (max-width: 600px) {
    margin: 8px;
    padding: 16px;
    border-radius: 6px;
  }
`;

/* Faixa de acoes abaixo do template full-bleed: os botoes continuam
   centralizados e com respiro mesmo com a proposta ocupando a viewport
   inteira (mesmo padrao do ActionWrapper do ContratoPublico). */
export const ActionArea = styled.div`
  max-width: 760px;
  margin: 16px auto 0;
  padding: 0 16px;
`;

export const Section = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

export const Label = styled.div`
  font-size: 0.75rem;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
  font-weight: 600;
`;

export const Value = styled.div`
  font-size: 1rem;
  color: #222;
  font-weight: 500;
`;

export const AmbienteCard = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #fafbfc;
  border: 1px solid #ececec;
  border-radius: 6px;
`;

export const AmbienteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;

  strong {
    font-size: 1rem;
    color: #1f2937;
  }
  span {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1f2937;
  }
`;

export const ItensTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th, td {
    padding: 6px 4px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }
  th {
    font-weight: 600;
    color: #555;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  td {
    color: #333;
  }
  tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    font-size: 0.78rem;
    th, td { padding: 4px 2px; }
  }
`;

export const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 2px solid #1f2937;
  margin-top: 8px;

  span {
    font-size: 1rem;
    color: #555;
    font-weight: 600;
  }
  strong {
    font-size: 1.5rem;
    color: #1f2937;
    font-weight: 800;
  }
`;

export const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const BaseButton = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.05s ease;
  min-height: 48px;  /* alvo de toque mobile */

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

export const ApproveButton = styled(BaseButton)`
  background: #16a34a;
  color: #fff;

  &:hover:not(:disabled) {
    background: #15803d;
  }
`;

/* "Revisar" NAO e recusa: o cliente esta pedindo ajuste, o orcamento volta
   pra negociacao. Cor comunica estado (guia do projeto) — vermelho dizia
   "destrutivo/perdido"; amarelo diz "atencao, precisa de ajuste". */
export const RejectButton = styled(BaseButton)`
  background: #d97706;
  color: #fff;

  &:hover:not(:disabled) {
    background: #b45309;
  }
`;

export const PrimaryButton = styled(BaseButton)`
  background: #1f2937;
  color: #fff;
  flex: 0 0 auto;
  padding: 10px 18px;
  font-size: 0.95rem;

  &:hover:not(:disabled) {
    background: #111827;
  }
`;

export const SecondaryButton = styled(BaseButton)`
  background: #e5e7eb;
  color: #1f2937;
  flex: 0 0 auto;
  padding: 10px 18px;
  font-size: 0.95rem;

  &:hover:not(:disabled) {
    background: #d1d5db;
  }
`;

const STATE_TONES = {
  success: { bg: "#dcfce7", color: "#166534", icon: "#16a34a" },
  warning: { bg: "#fef3c7", color: "#92400e", icon: "#f59e0b" },
  danger: { bg: "#fee2e2", color: "#991b1b", icon: "#dc2626" },
  neutral: { bg: "#f3f4f6", color: "#374151", icon: "#6b7280" },
  info: { bg: "#dbeafe", color: "#1e40af", icon: "#2563eb" },
};

export const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 16px;
  background: ${({ $tone }) => STATE_TONES[$tone || "neutral"].bg};
  color: ${({ $tone }) => STATE_TONES[$tone || "neutral"].color};
  border-radius: 6px;
  text-align: center;

  svg {
    color: ${({ $tone }) => STATE_TONES[$tone || "neutral"].icon};
  }
  h2 {
    margin: 0;
    font-size: 1.2rem;
  }
  p {
    margin: 0;
    font-size: 0.95rem;
    max-width: 480px;
  }
`;

export const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 100;
`;

export const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);

  h3 {
    margin: 0 0 12px 0;
    font-size: 1.1rem;
  }
  p {
    margin: 0 0 12px 0;
    color: #444;
    font-size: 0.95rem;
  }
`;

export const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 12px;
  min-height: 90px;

  &:focus {
    outline: none;
    border-color: #1f2937;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 12px;
`;
